import type {
  FaceComposite,
  FaceKey,
  FacePhotoSize,
  ResolvedFaceComposite,
} from '@repo/api-contracts';

/** 면 합성 에셋 종류 — 마스크(필수)·음영·안내선(ADR-0007 D6·D14). 색상과 무관한 면 공통 값이다. */
export const COMPOSITE_ASSET_KINDS = ['mask', 'shading', 'safeArea'] as const;
export type CompositeAssetKind = (typeof COMPOSITE_ASSET_KINDS)[number];

/**
 * 합성 설정의 저장 모양 — 키·크기·multiply 만 둔다. 응답이 덧붙인 표시 URL(maskUrl·shadingUrl·safeAreaUrl)은 빼서
 * 초안 밖 URL 맵(photoUrls)에 둔다. 사진의 photoOnly 와 같은 규칙이라 URL 이 바뀌어도 미저장 변경이 되지 않는다.
 */
export const compositeOnly = ({
  multiply,
  maskKey,
  maskSize,
  shadingKey,
  shadingSize,
  safeAreaKey,
  safeAreaSize,
}: FaceComposite): FaceComposite => ({
  multiply,
  maskKey,
  maskSize: { ...maskSize },
  ...(shadingKey === undefined || shadingSize === undefined
    ? {}
    : { shadingKey, shadingSize: { ...shadingSize } }),
  ...(safeAreaKey === undefined || safeAreaSize === undefined
    ? {}
    : { safeAreaKey, safeAreaSize: { ...safeAreaSize } }),
});

/** 합성 설정에서 그 종류의 저장 키·크기. 없는 음영·안내선은 undefined 다. */
export const compositeAssetOf = (
  composite: FaceComposite | undefined,
  kind: CompositeAssetKind,
): { readonly key: string; readonly size: FacePhotoSize } | undefined => {
  if (composite === undefined) return undefined;
  if (kind === 'mask') return { key: composite.maskKey, size: composite.maskSize };
  const [key, size] =
    kind === 'shading'
      ? [composite.shadingKey, composite.shadingSize]
      : [composite.safeAreaKey, composite.safeAreaSize];
  return key === undefined || size === undefined ? undefined : { key, size };
};

/** 합성 설정이 쓰는 저장 키. URL 갱신은 초안에 있는 키의 URL 만 받는다. */
export const compositeAssetKeys = (composite: FaceComposite): string[] => [
  composite.maskKey,
  ...(composite.shadingKey === undefined ? [] : [composite.shadingKey]),
  ...(composite.safeAreaKey === undefined ? [] : [composite.safeAreaKey]),
];

const urlEntry = (key: string | undefined, url: string | undefined): [string, string][] =>
  key === undefined || url === undefined ? [] : [[key, url]];

/** 응답 합성 설정의 저장 키 → 표시 URL. */
export const compositeAssetUrls = (composite: ResolvedFaceComposite): [string, string][] => [
  ...urlEntry(composite.maskKey, composite.maskUrl),
  ...urlEntry(composite.shadingKey, composite.shadingUrl),
  ...urlEntry(composite.safeAreaKey, composite.safeAreaUrl),
];

const COMPOSITE_SLOT_PREFIX = 'composite:';

/**
 * 합성 업로드 작업 칸(operations 의 키). 사진 작업 칸(`{색상}:{면}`)과 같은 표에 둬서 저장 버전 이동 확인·이탈 보호가
 * 함께 본다. 면을 끝에 두어 면을 끌 때의 정리(`:{면}` 으로 끝나는 칸 삭제)가 그 면 합성 작업도 지운다.
 */
export const compositeSlotKey = (face: FaceKey, kind: CompositeAssetKind): string =>
  `${COMPOSITE_SLOT_PREFIX}${kind}:${face}`;

export const isCompositeSlot = (key: string): boolean => key.startsWith(COMPOSITE_SLOT_PREFIX);

/** 그 면의 합성 업로드 작업 칸 키(composite:*:{면}). 표면·마스크 삭제 확인창의 '진행 중인 업로드 N개' 가 센다(D-E). */
export const compositeSlotsOf = (
  operations: Readonly<Record<string, unknown>>,
  face: FaceKey,
): string[] =>
  Object.keys(operations).filter((key) => isCompositeSlot(key) && key.endsWith(`:${face}`));

/** 그 면의 합성 업로드 작업 칸을 뺀 작업 표. 표면·마스크를 지울 때 진행 중 합성 업로드를 함께 취소한다(D-E). */
export const withoutCompositeOperations = <T>(
  operations: Readonly<Record<string, T>>,
  face: FaceKey,
): Readonly<Record<string, T>> => {
  const removed = new Set(compositeSlotsOf(operations, face));
  return Object.fromEntries(Object.entries(operations).filter(([key]) => !removed.has(key)));
};
